import { PerformanceMetric, usePerformance } from '@/context/performance';
import { useBackgroundAppBundle } from '@/context/background-app-bundle';
import { setChildAppLoaded } from '@customerio/hydra-nav-state';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { AppBundle } from '../../utils/app-bundles';
import { PageLoader } from '@customerio/pluma-components/react';

interface AppBundleLoaderProps {
  appBundle: AppBundle;
  className?: string;
  renderApp: (appWrapper: HTMLDivElement) => Promise<() => void>;
  style?: string;
}

/** Loads an app bundle, and keeps it mounted in the background while the user is viewing another app,
 * to improve performance when switching between different apps.
 *
 * This is done to provide a seamless experience for the user, as the borders between the different apps
 * are invisible from the user's perspective.
 */
export function AppBundleLoader({ appBundle, className = '', renderApp, style }: AppBundleLoaderProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [loaded, setIsLoaded] = useState(false);
  const recordPerformanceMetric = usePerformance();
  const { getData: getBackgroundAppData, setData: setBackgroundAppData } = useBackgroundAppBundle(appBundle);

  useEffect(() => {
    setChildAppLoaded(false);
  }, []);

  function hideAppWrapper() {
    const appData = getBackgroundAppData();
    const appWrapper = appData?.appWrapper;
    if (appWrapper != null) {
      appWrapper.style.display = 'none';
    }
  }

  function showAppWrapper() {
    const appData = getBackgroundAppData();
    const appWrapper = appData?.appWrapper;
    if (appWrapper != null) {
      appWrapper.style.display = 'flex';
    }
  }

  const load = useCallback(async () => {
    recordPerformanceMetric(PerformanceMetric.ChildAppLoadStart);

    const appWrapperTemplate = document.createElement('template');
    appWrapperTemplate.innerHTML = `<div class="${className}" style="display: none; min-width: 0; flex: 1; ${
      style ?? ''
    }"></div>`;

    const appWrapper = appWrapperTemplate.content.cloneNode(true).firstChild as HTMLDivElement;

    setBackgroundAppData({
      initialized: true,
      loaded: false,
      appWrapper,
    });

    if (ref.current != null) {
      ref.current.parentElement?.appendChild(appWrapper);
    }

    const cleanup = await renderApp(appWrapper);

    setBackgroundAppData({
      loaded: true,
      cleanup,
    });
  }, [appBundle, className, renderApp, recordPerformanceMetric, setBackgroundAppData]);

  /** When this component is rendered for the first time, it loads the app bundle, if it hasn't been loaded yet, and displays it.
   * When this component is unmounted, it hides the app wrapper (and the app itself), instead of destroying it.
   * The next times this component is rendered, it will display the previous app wrapper again, instead of re-loading and re-rendering it.
   * The effect below controls this behavior.
   */
  useEffect(() => {
    const appData = getBackgroundAppData();

    if (appData?.initialized !== true) {
      setBackgroundAppData({
        loadPromise: load(),
      });
    }

    const loadPromise = getBackgroundAppData().loadPromise;
    let cancelLoadListener = false;
    if (loadPromise != null && !loaded) {
      loadPromise.then(() => {
        // If the user switches to a different app bundle while this app is loaded,
        if (cancelLoadListener) {
          return;
        }

        setChildAppLoaded(true);
        setIsLoaded(true);
        showAppWrapper();
      });
    } else if (loaded) {
      showAppWrapper();
    }

    return () => {
      hideAppWrapper();
      cancelLoadListener = true;
    };
  }, [
    className,
    renderApp,
    getBackgroundAppData,
    load,
    loaded,
    setChildAppLoaded,
    setIsLoaded,
    showAppWrapper,
    hideAppWrapper,
  ]);

  return (
    <>
      <div ref={ref} style={{ display: 'none' }}></div>
      {loaded ? null : <PageLoader />}
    </>
  );
}
